import { FC, useEffect, useRef, useState } from 'react';
import { Button, ConfigProvider, Form, Input, InputNumber, Modal, Radio, Select, Switch, Tabs } from 'antd';
import { useMethods } from '@lhb/hook';
import dayjs from 'dayjs';
import V2FormMultipleDatePicker from '@/common/components/Form/V2FormMultipleDatePicker/V2FormMultipleDatePicker';
import V2FormDatePicker from '@/common/components/Form/V2FormDatePicker/V2FormDatePicker';
import V2Form from '@/common/components/Form/V2Form';
import V2FormTextArea from '@/common/components/Form/V2FormTextArea/V2FormTextArea';
import V2Fuzzy, { FuzzyHandles } from '@/common/components/Form/V2Fuzzy';
import V2Message from '@/common/components/Others/V2Hint/V2Message';
import { placeList } from '@/common/api/common';
import { employeeListPermission } from '@/common/api/employee';
import { getChancePointPlaceSpots } from '@/common/api/expandStore/chancepointPlaceSpot';
import { checkSpotFloorSelection, createCheckSpotResourceSpot, createCheckSpotTask, getCheckSpotCompetitorBrands } from '@/common/api/expandStore/checkSpotTaskCreate';
import { buildCheckSpotTask, CollectorValue, TaskFormValues, syncFlowDates, isPastCollectDate } from './CreateCheckSpotTaskModal.form';
import styles from './CreateCheckSpotTaskModal.module.less';
import CheckSpotPeriodSelector from './CheckSpotPeriodSelector';

export interface CreatedCheckSpotTask {
  id: number;
  name: string;
  projectId?: number;
  projectName?: string;
  spotId?: number;
  spotName?: string;
}

/** 采集人字段独立维护已确认值，切换 Tab 时不混用员工与服务商身份。 */
const CollectorSelect: FC<{ value?: CollectorValue; onChange?: (value?: CollectorValue) => void; batch?: boolean; disabled?: boolean }> = ({ value, onChange, batch = false, disabled }) => {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState('1');
  const [employee, setEmployee] = useState<{ value: number; label: string }>();
  const methods = useMethods({
    show() {
      setType(String(value?.collectorType || 1));
      setEmployee(value?.collectorId ? { value: value.collectorId, label: value.collectorName || '' } : undefined);
      setOpen(true);
    },
    close() { setOpen(false); },
    tab(key: string) { setType(key); },
    selectEmployee(option) { setEmployee(option); },
    load(keyword: string) {
      return employeeListPermission({ keyword, page: 1, size: 50 }).then(res => {
        const list = res?.data || res?.objectList || [];
        return list.map(item => ({ value: item.id, label: item.name }));
      });
    },
    confirm() {
      if (type === '1' && !employee) { V2Message.warning('请选择采集人'); return; }
      onChange?.(type === '2' ? { collectorType: 2 } : {
        collectorType: 1, collectorId: employee!.value, collectorName: employee!.label,
      });
      setOpen(false);
    },
  });
  return <>
    <Button block={!batch} type={batch ? 'link' : 'default'} disabled={disabled} onClick={methods.show}>{batch ? '批量选择采集人' : value?.collectorType === 2 ? '可计算开店' : value?.collectorName || '请选择采集人'}</Button>
    {open && <Modal open title={batch ? '批量选择采集人' : '选择采集人'} width={520} onCancel={methods.close} onOk={methods.confirm} destroyOnClose getContainer={false}>
      <Tabs activeKey={type} onChange={methods.tab} items={[
        { key: '1', label: '自家员工', children: <V2Fuzzy fieldNames={{ label: 'label', value: 'value' }} value={employee} labelInValue loadData={methods.load} onChange={methods.selectEmployee} placeholder='输入员工名称搜索' style={{ width: '100%' }} /> },
        { key: '2', label: '服务商踩点', children: <Radio checked>可计算开店（服务商通过任务码领取任务）</Radio> },
      ]} />
    </Modal>}
  </>;
};

const CreateSpotModal: FC<{ projectId: number; onCancel: () => void; onCreated: (spot: { value: number; label: string }) => void }> = ({ projectId, onCancel, onCreated }) => {
  const [form] = Form.useForm();
  const [floors, setFloors] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const lock = useRef(false);
  useEffect(() => { checkSpotFloorSelection().then(res => setFloors(Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []))); }, []);
  const methods = useMethods({
    cancel() { if (!lock.current) onCancel(); },
    submit() {
      if (lock.current) return;
      lock.current = true;
      form.validateFields().then(values => {
        setSaving(true);
        return createCheckSpotResourceSpot({ ...values, placeId: projectId, heightLimit: values.heightLimit === undefined ? '' : String(values.heightLimit) }).then(res => {
          const id = res?.data ?? res;
          if (typeof id !== 'number' || id <= 0) { V2Message.error('创建点位失败，请重试'); return; }
          onCreated({ value: id, label: values.spotName.trim() });
        });
      }, () => undefined).catch(() => { /* 请求层统一提示；保留表单便于重试。 */ }).finally(() => { lock.current = false; setSaving(false); });
    },
  });
  return <Modal open title='创建点位' width={540} getContainer={false} onCancel={methods.cancel} onOk={methods.submit} confirmLoading={saving} maskClosable={false}>
    <V2Form form={form} disabled={saving} layout='vertical'>
      <Form.Item name='spotCategoryId' label='点位类型' rules={[{ required: true, message: '请选择点位类型' }]}><Select options={[{ value: 62, label: '展位' }, { value: 102, label: '铺位' }]} /></Form.Item>
      <Form.Item name='spotName' label='点位名称' rules={[{ required: true, whitespace: true, message: '请输入点位名称' }]}><Input maxLength={100} /></Form.Item>
      <Form.Item name='spotFloor' label='点位楼层' rules={[{ required: true, message: '请选择楼层' }]}><Select options={floors.map(item => ({ value: item.id, label: item.name }))} /></Form.Item>
      <div className={styles.spotDimensions}>
        <Form.Item name='spotLength' label='长（m）' rules={[{ required: true, message: '请输入长' }, { type: 'number', min: 0.01, message: '长须大于0' }]}><InputNumber min={0.01} /></Form.Item>
        <Form.Item name='spotWidth' label='宽（m）' rules={[{ required: true, message: '请输入宽' }, { type: 'number', min: 0.01, message: '宽须大于0' }]}><InputNumber min={0.01} /></Form.Item>
        <Form.Item name='heightLimit' label='高（m）'><InputNumber min={0.01} /></Form.Item>
      </div>
    </V2Form>
  </Modal>;
};

/** 创建独立资源；与机会点的关联仅由父弹窗确认后整体保存。 */
const CreateCheckSpotTaskModal: FC<{ onCancel: () => void; onCreated: (task: CreatedCheckSpotTask) => void }> = ({ onCancel, onCreated }) => {
  const [form] = Form.useForm();
  const project = Form.useWatch('projectId', form);
  const flowEnabled = Form.useWatch('flowEnabled', form);
  const brandEnabled = Form.useWatch('brandEnabled', form);
  const brandMode = Form.useWatch('brandMode', form);
  const competitorEnabled = Form.useWatch('competitorEnabled', form);
  const competitorMode = Form.useWatch('competitorMode', form);
  const flowRecords: NonNullable<TaskFormValues['flowRecords']> = Form.useWatch('flowRecords', form) || [];
  const canFollowFlow = !!flowEnabled && flowRecords.length > 0;
  const flowDatesKey = flowRecords.map(record => record.date).join(',');
  const firstCollectorKey = JSON.stringify(flowRecords[0]?.collector);
  const modeTouched = useRef(false);
  const collectorTouched = useRef(false);
  const competitorModeTouched = useRef(false);
  const competitorCollectorTouched = useRef(false);
  const [createSpot, setCreateSpot] = useState(false);
  const [saving, setSaving] = useState(false);
  const lock = useRef(false);
  const spotRef = useRef<FuzzyHandles>(null);
  const projectOptions = useRef<any[]>([]);
  const methods = useMethods({
    clearBrandData() {
      form.setFieldsValue({ brandDate: undefined, brandCollector: undefined });
      collectorTouched.current = false;
    },
    syncBrandFlowData() {
      const dates = flowRecords.map(record => record.date);
      const currentDate = form.getFieldValue('brandDate');
      form.setFieldValue('brandDate', dates.includes(currentDate) ? currentDate : dates[0]);
      // 与 H5 一致，已带入或手动选定的采集人不被后续客流修改覆盖。
      if (form.getFieldValue('brandCollector')) collectorTouched.current = true;
      if (!collectorTouched.current) form.setFieldValue('brandCollector', flowRecords[0]?.collector);
    },
    changeBrandMode(event) {
      modeTouched.current = true;
      if (event.target.value === 'flow') {
        collectorTouched.current = false;
        methods.syncBrandFlowData();
      } else {
        methods.clearBrandData();
      }
    },
    clearCompetitorData() {
      form.setFieldsValue({ competitorDate: undefined, competitorCollector: undefined });
      competitorCollectorTouched.current = false;
    },
    syncCompetitorFlowData() {
      const dates = flowRecords.map(record => record.date);
      const currentDate = form.getFieldValue('competitorDate');
      form.setFieldValue('competitorDate', dates.includes(currentDate) ? currentDate : dates[0]);
      // 与 H5 一致，已带入或手动选定的采集人不被后续客流修改覆盖。
      if (form.getFieldValue('competitorCollector')) competitorCollectorTouched.current = true;
      if (!competitorCollectorTouched.current) form.setFieldValue('competitorCollector', flowRecords[0]?.collector);
    },
    changeCompetitorMode(event) {
      competitorModeTouched.current = true;
      if (event.target.value === 'flow') {
        competitorCollectorTouched.current = false;
        methods.syncCompetitorFlowData();
      } else {
        methods.clearCompetitorData();
      }
    },
    changeValues(changed: Partial<TaskFormValues>) {
      if ('competitorCollector' in changed) competitorCollectorTouched.current = true;
      if (competitorMode === 'custom' && ('competitorDate' in changed || 'competitorCollector' in changed)) competitorModeTouched.current = true;
      if ('brandCollector' in changed) collectorTouched.current = true;
      if (brandMode === 'custom' && ('brandDate' in changed || 'brandCollector' in changed)) modeTouched.current = true;
    },
    popupContainer(trigger?: HTMLElement) {
      return trigger?.closest<HTMLElement>('.ant-modal-content') || document.body;
    },
    batchCollector(collector?: CollectorValue) {
      // 与 H5 一致，仅确认时覆盖当前日期；后续新增日期仍需选择采集人。
      const records: NonNullable<TaskFormValues['flowRecords']> = form.getFieldValue('flowRecords') || [];
      form.setFieldValue('flowRecords', records.map(record => ({ ...record, collector: collector ? { ...collector } : undefined })));
    },
    getDateValue(dates: number[]) {
      const nextRecords = syncFlowDates(dates.map(date => dayjs(date).format('YYYY-MM-DD')), form.getFieldValue('flowRecords'));
      form.setFieldValue('flowRecords', nextRecords);
      return nextRecords.map(record => record.date);
    },
    getDateProps(dates: string[] = []) { return { value: dates.map(date => dayjs(date).valueOf()) }; },
    getBrandDateValue(date) { return date ? date.format('YYYY-MM-DD') : undefined; },
    getBrandDateProps(date?: string) { return { value: date ? dayjs(date) : undefined }; },
    projects(keyword = '') {
      return placeList({ name: keyword, page: 1, size: 50, shopCategory: 1 }).then(res => {
        const list = res?.objectList || res?.data || [];
        projectOptions.current = list;
        return list.map(item => ({ value: item.id || item.projectId, label: item.name || item.projectName }));
      });
    },
    competitorBrands(keyword = '') {
      return getCheckSpotCompetitorBrands({ name: keyword, page: 1, size: 20 }).then(res => {
        const list = Array.isArray(res?.data) ? res.data : [];
        return list.map(item => ({ value: item.id, label: item.name }));
      });
    },
    spots(keyword = '') {
      if (!project?.value) return Promise.resolve([]);
      return getChancePointPlaceSpots({ placeId: project.value }).then(res => {
        const list = Array.isArray(res) ? res : res?.data || [];
        return list.filter(item => (item.spotName || '').includes(keyword)).map(item => ({ value: item.spotId, label: item.spotName }));
      });
    },
    changeProject() { form.setFieldValue('spotId', undefined); },
    openSpot() { setCreateSpot(true); },
    closeSpot() { setCreateSpot(false); },
    createdSpot(spot: { value: number; label: string }) {
      spotRef.current?.addOption(spot);
      form.setFieldValue('spotId', spot);
      setCreateSpot(false);
    },
    cancel() { if (!lock.current) onCancel(); },
    submit() {
      if (lock.current) return;
      lock.current = true;
      form.validateFields().then((values: TaskFormValues) => {
        let payload;
        try { payload = buildCheckSpotTask(values); } catch (error) { V2Message.warning((error as Error).message); return; }
        const projectOption = projectOptions.current.find(item => String(item.id || item.projectId) === String(values.projectId.value));
        setSaving(true);
        return createCheckSpotTask({ ...payload, spotAddress: projectOption?.address?.address, lng: projectOption?.address?.longitude, lat: projectOption?.address?.latitude }).then(res => {
          const task = res?.data ?? res;
          if (!task?.id) { V2Message.error('创建任务失败，请重试'); return; }
          onCreated({ ...payload, ...task, name: task.name || payload.spotName });
        });
      }, () => undefined).catch(() => { /* 请求层统一提示；保留表单便于重试。 */ }).finally(() => { lock.current = false; setSaving(false); });
    },
  });
  useEffect(() => {
    if (!canFollowFlow) {
      if (form.getFieldValue('brandMode') !== 'custom') methods.clearBrandData();
      form.setFieldValue('brandMode', 'custom');
      return;
    }
    if (!modeTouched.current && form.getFieldValue('brandMode') !== 'flow') form.setFieldValue('brandMode', 'flow');
    if (form.getFieldValue('brandMode') === 'flow') methods.syncBrandFlowData();
  }, [canFollowFlow, flowDatesKey, firstCollectorKey, brandMode, brandEnabled]);
  useEffect(() => {
    if (!canFollowFlow) {
      if (form.getFieldValue('competitorMode') !== 'custom') methods.clearCompetitorData();
      form.setFieldValue('competitorMode', 'custom');
      return;
    }
    if (!competitorModeTouched.current && form.getFieldValue('competitorMode') !== 'flow') form.setFieldValue('competitorMode', 'flow');
    if (form.getFieldValue('competitorMode') === 'flow') methods.syncCompetitorFlowData();
  }, [canFollowFlow, flowDatesKey, firstCollectorKey, competitorMode, competitorEnabled]);
  return <ConfigProvider getPopupContainer={methods.popupContainer}><Modal open title='新增踩点任务' width={680} getContainer={false} onCancel={methods.cancel} onOk={methods.submit} confirmLoading={saving} maskClosable={false} destroyOnClose>
    <V2Form form={form} layout='vertical' disabled={saving} className={styles.form} onValuesChange={methods.changeValues} initialValues={{ flowEnabled: true, brandEnabled: true, brandMode: 'custom', competitorEnabled: true, competitorMode: 'custom', competitors: [], flowCollectDates: [], flowRecords: [], periods: [] }}>
      <Form.Item name='projectId' label='项目踩点' rules={[{ required: true, message: '请选择项目' }]}><V2Fuzzy fieldNames={{ label: 'label', value: 'value' }} labelInValue placeholder='输入项目名称搜索' loadData={methods.projects} onChange={methods.changeProject} /></Form.Item>
      <Form.Item className={styles.spotField} name='spotId' label='踩点点位' rules={[{ required: true, message: '请选择点位' }]} extra={project?.value && <Button type='link' onClick={methods.openSpot}>+ 创建点位</Button>}><V2Fuzzy fieldNames={{ label: 'label', value: 'value' }} key={project?.value || 'empty'} ref={spotRef} labelInValue loadData={methods.spots} disabled={!project?.value} placeholder={project?.value ? '请选择点位' : '请先选择项目'} /></Form.Item>
      <V2FormTextArea name='remark' label='备注' maxLength={500} config={{ showCount: true, rows: 3 }} />
      <section className={styles.module}>
        <Form.Item name='flowEnabled' label='客流信息' valuePropName='checked'><Switch /></Form.Item>
        {flowEnabled && <>
          <V2FormMultipleDatePicker
            name='flowCollectDates'
            label='采集日期'
            placeholder='请选择采集日期'
            disabled={saving}
            config={{ datePickerProps: { disabledDate: isPastCollectDate }, defaultIsRange: false }}
            formItemConfig={{ getValueFromEvent: methods.getDateValue, getValueProps: methods.getDateProps }}
          />
          <Form.List name='flowRecords'>{fields => <>
            {fields.map(field => <div key={flowRecords[field.name]?.date || field.key} className={styles.row}>
              <Form.Item name={[field.name, 'date']} hidden><Input /></Form.Item>
              <Form.Item name={[field.name, 'collector']} label={<div className={styles.collectorLabel}>
                <span>{`${flowRecords[field.name]?.date || ''}-采集人`}</span>
                {field.name === 0 && fields.length > 1 && <CollectorSelect batch disabled={saving} value={flowRecords[0]?.collector} onChange={methods.batchCollector} />}
              </div>}><CollectorSelect /></Form.Item>
            </div>)}
          </>}</Form.List>
          <Form.Item name='periods' label='采集时段（多日期共用）'>
            <CheckSpotPeriodSelector disabled={saving} />
          </Form.Item>
        </>}
      </section>
      <section className={styles.module}>
        <Form.Item name='brandEnabled' label='周边信息' valuePropName='checked'><Switch /></Form.Item>
        {brandEnabled && <>
          <Form.Item name='brandMode' label='采集方式'><Radio.Group onChange={methods.changeBrandMode} options={[{ value: 'flow', label: '随客流信息一同采集', disabled: !canFollowFlow }, { value: 'custom', label: '自定义日期采集' }]} /></Form.Item>
          {brandMode === 'flow' && canFollowFlow ? <Form.Item label='采集日期'>
            <div className={styles.flowDateDisplay}>
              <span>{flowRecords.map(record => dayjs(record.date).format('YYYY.MM.DD')).join('、')}</span>
              {flowRecords.length > 1 && <span className={styles.flowDateSuffix}>任选一天</span>}
            </div>
          </Form.Item> : <V2FormDatePicker name='brandDate' label='采集日期' disabled={saving} config={{ disabledDate: isPastCollectDate }} formItemConfig={{ getValueFromEvent: methods.getBrandDateValue, getValueProps: methods.getBrandDateProps }} />}
          <Form.Item name='brandCollector' label='采集人'><CollectorSelect /></Form.Item>
        </>}
      </section>
      <section className={styles.module}>
        <Form.Item name='competitorEnabled' label='竞品调研' valuePropName='checked'><Switch /></Form.Item>
        {competitorEnabled && <>
          <Form.Item name='competitors' label='竞品品牌'><V2Fuzzy fieldNames={{ label: 'label', value: 'value' }} mode='multiple' labelInValue placeholder='请选择竞品品牌' loadData={methods.competitorBrands} /></Form.Item>
          <Form.Item name='competitorMode' label='采集方式'><Radio.Group onChange={methods.changeCompetitorMode} options={[{ value: 'flow', label: '随客流信息一同采集', disabled: !canFollowFlow }, { value: 'custom', label: '自定义日期采集' }]} /></Form.Item>
          {competitorMode === 'flow' && canFollowFlow ? <Form.Item label='采集日期'>
            <div className={styles.flowDateDisplay}>
              <span>{flowRecords.map(record => dayjs(record.date).format('YYYY.MM.DD')).join('、')}</span>
              {flowRecords.length > 1 && <span className={styles.flowDateSuffix}>任选一天</span>}
            </div>
          </Form.Item> : <V2FormDatePicker name='competitorDate' label='采集日期' disabled={saving} config={{ disabledDate: isPastCollectDate }} formItemConfig={{ getValueFromEvent: methods.getBrandDateValue, getValueProps: methods.getBrandDateProps }} />}
          <Form.Item name='competitorCollector' label='采集人'><CollectorSelect /></Form.Item>
        </>}
      </section>
    </V2Form>
    {createSpot && project?.value && <CreateSpotModal projectId={project.value} onCancel={methods.closeSpot} onCreated={methods.createdSpot} />}
  </Modal></ConfigProvider>;
};

export default CreateCheckSpotTaskModal;
